import { useState } from 'react';
import type { CampoPropio, Etiqueta } from '../../../api/ops/clientes';
import { Ayuda } from '../../../componentes/Ayuda';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { AYUDA } from '../lista/textos';
import { formularioVacio, validar, type DatosFormulario, type ErroresFormulario } from './datos';
import { Formulario } from './Formulario';

interface Props {
    abierto: boolean;
    movil: boolean;
    campos: readonly CampoPropio[];
    etiquetas: readonly Etiqueta[];
    /** Lo que ya traía (el teléfono buscado) o lo que se escribió si la API lo rechazó al enviarlo. */
    inicial: { datos: DatosFormulario; errores: ErroresFormulario } | null;
    onCerrar: () => void;
    /** Sin información comercial: se da de alta con «Deshacer» 5 s (G159). */
    onDarDeAlta: (d: DatosFormulario) => void;
    /** Con información comercial: avisa al cliente, se envía ya y sin «Deshacer» (G159, G162). */
    onDarDeAltaYAvisar: (d: DatosFormulario) => Promise<void>;
}

/**
 * Alta de cliente (2.2) en RV Panel: nombre, teléfono, email, Instagram, idioma, fecha de nacimiento con su casilla (G241),
 * campos propios, etiquetas e información comercial (N2). Con la información comercial marcada, una ventana avisa de que se
 * le pedirá confirmarla (G162).
 */
export function Alta({ abierto, movil, campos, etiquetas, inicial, onCerrar, onDarDeAlta, onDarDeAltaYAvisar }: Props) {
    const [datos, setDatos] = useState<DatosFormulario>(inicial?.datos ?? formularioVacio());
    const [intentado, setIntentado] = useState(inicial !== null && Object.keys(inicial.errores).length > 0);
    const [deApi, setDeApi] = useState<ErroresFormulario>(inicial?.errores ?? {});
    const [ventana, setVentana] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    const errores: ErroresFormulario = intentado ? { ...validar(datos, campos, true), ...deApi } : {};
    const canales = [...(datos.comercial.whatsapp ? ['WhatsApp'] : []), ...(datos.comercial.email ? ['email'] : [])];

    const darDeAlta = () => {
        setIntentado(true);
        setDeApi({});
        if (Object.keys(validar(datos, campos, true)).length > 0) {
            return;
        }
        if (canales.length > 0) {
            setVentana(true);
            return;
        }
        onDarDeAlta(datos);
    };

    const avisar = () => {
        setOcupado(true);
        onDarDeAltaYAvisar(datos)
            .then(() => {
                setVentana(false);
            })
            .catch(() => {
                setVentana(false);
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <>
            <PanelLateral
                abierto={abierto}
                onCerrar={onCerrar}
                movil={movil}
                icono="user-plus"
                eyebrow="Alta de cliente"
                titulo="Nuevo cliente"
                subtitulo="Queda como cliente de tu negocio."
                accion="Dar de alta"
                accionIcono="user-plus"
                onAccion={darDeAlta}
            >
                <div className="rv-cl-ayuda-panel">
                    <Ayuda tarjetas={AYUDA.alta} />
                </div>
                <Formulario
                    datos={datos}
                    onCambio={(d) => {
                        setDatos(d);
                        setDeApi({});
                    }}
                    errores={errores}
                    campos={campos}
                    etiquetas={etiquetas}
                    alta
                />
            </PanelLateral>
            <Ventana
                abierta={ventana}
                titulo="Dar de alta y pedirle que lo confirme"
                texto={`Le enviamos un mensaje por ${canales.join(' y por ')} para que confirme que quiere recibir información comercial. Hasta que lo confirme, solo le llegan confirmaciones y avisos.`}
                accion="Dar de alta"
                accionIcono="user-plus"
                ocupado={ocupado}
                ocupadoTexto="Dando de alta…"
                movil={movil}
                onAccion={avisar}
                onCerrar={() => {
                    setVentana(false);
                }}
            />
        </>
    );
}
